import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, currency, formatApiError, resolveImage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { Package, ShoppingBag, Users, TrendingUp, Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import ImageDropzone from "@/components/ImageDropzone";

const empty = {
  name: "",
  slug: "",
  category: "Hair Oils",
  price: 0,
  compare_at_price: null,
  short_description: "",
  description: "",
  ingredients: "",
  benefits: [],
  image_url: "",
  gallery: [],
  stock: 100,
  featured: false,
  tags: [],
};

export default function Admin() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [benefitsInput, setBenefitsInput] = useState("");

  const loadAll = async () => {
    try {
      const [s, p, o, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/products"),
        api.get("/admin/orders"),
        api.get("/categories"),
      ]);
      setStats(s.data);
      setProducts(p.data);
      setOrders(o.data);
      setCategories(c.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (user?.role === "admin") loadAll();
  }, [user]);

  if (loading) return <div className="p-24 text-center text-[#8a8d86]">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setBenefitsInput("");
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p });
    setBenefitsInput((p.benefits || []).join(", "));
    setDialogOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      price: Number(form.price),
      stock: Number(form.stock),
      compare_at_price: form.compare_at_price ? Number(form.compare_at_price) : null,
      benefits: benefitsInput
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    };
    try {
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      }
      setDialogOpen(false);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Deleted");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const updateOrderStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, null, { params: { status } });
      toast.success(`Order marked ${status}`);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#f4f4f5]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#8a8d86]">Admin</p>
            <h1 className="mt-1 font-serif text-4xl text-[#09090b] tracking-tight">
              Dashboard
            </h1>
          </div>
        </div>

        {/* STATS */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard
            icon={<TrendingUp className="h-4 w-4" strokeWidth={1.5} />}
            label="Revenue"
            value={currency(stats?.revenue || 0)}
            testid="stat-revenue"
          />
          <StatCard
            icon={<ShoppingBag className="h-4 w-4" strokeWidth={1.5} />}
            label="Orders"
            value={stats?.total_orders || 0}
            testid="stat-orders"
          />
          <StatCard
            icon={<Package className="h-4 w-4" strokeWidth={1.5} />}
            label="Products"
            value={stats?.total_products || 0}
            testid="stat-products"
          />
          <StatCard
            icon={<Users className="h-4 w-4" strokeWidth={1.5} />}
            label="Customers"
            value={stats?.total_customers || 0}
            testid="stat-customers"
          />
        </div>

        <Tabs defaultValue="products">
          <TabsList className="bg-white border border-[#e4e4e7]">
            <TabsTrigger value="products" data-testid="admin-tab-products">Products</TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders</TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="mt-6">
            <div className="bg-white border border-[#e4e4e7] rounded-md">
              <div className="p-6 flex items-center justify-between border-b border-[#e4e4e7]">
                <h2 className="font-serif text-2xl">Products</h2>
                <Button
                  data-testid="admin-new-product-btn"
                  onClick={openCreate}
                  className="rounded-full bg-[#09090b] hover:bg-black text-white h-10"
                >
                  <Plus className="h-4 w-4 mr-1" /> New product
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#fafafa] text-[#71717a] text-xs uppercase tracking-widest">
                    <tr>
                      <th className="text-left p-4">Product</th>
                      <th className="text-left p-4">Category</th>
                      <th className="text-left p-4">Price</th>
                      <th className="text-left p-4">Stock</th>
                      <th className="text-left p-4">Featured</th>
                      <th className="text-right p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p) => (
                      <tr key={p.id} className="border-t border-[#e4e4e7]" data-testid={`admin-product-row-${p.slug}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded bg-[#efeadd] overflow-hidden">
                              {p.image_url && (
                                <img src={resolveImage(p.image_url)} alt="" className="w-full h-full object-cover" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium">{p.name}</p>
                              <p className="text-xs text-[#71717a]">{p.slug}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-[#52525b]">{p.category}</td>
                        <td className="p-4">{currency(p.price)}</td>
                        <td className="p-4">{p.stock}</td>
                        <td className="p-4">
                          <span
                            className={
                              "text-xs px-2 py-1 rounded-full " +
                              (p.featured ? "bg-[#1e392a]/10 text-[#1e392a]" : "bg-[#f4f4f5] text-[#71717a]")
                            }
                          >
                            {p.featured ? "Yes" : "No"}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <div className="inline-flex gap-2">
                            <Button
                              data-testid={`admin-edit-${p.slug}`}
                              size="sm"
                              variant="ghost"
                              onClick={() => openEdit(p)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              data-testid={`admin-delete-${p.slug}`}
                              size="sm"
                              variant="ghost"
                              onClick={() => remove(p)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            <div className="bg-white border border-[#e4e4e7] rounded-md">
              <div className="p-6 border-b border-[#e4e4e7]">
                <h2 className="font-serif text-2xl">Orders</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#fafafa] text-[#71717a] text-xs uppercase tracking-widest">
                    <tr>
                      <th className="text-left p-4">Order</th>
                      <th className="text-left p-4">Customer</th>
                      <th className="text-left p-4">Total</th>
                      <th className="text-left p-4">Payment</th>
                      <th className="text-left p-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => (
                      <tr key={o.id} className="border-t border-[#e4e4e7]" data-testid={`admin-order-row-${o.order_number}`}>
                        <td className="p-4">
                          <p className="font-medium">{o.order_number}</p>
                          <p className="text-xs text-[#71717a]">
                            {new Date(o.created_at).toLocaleString()}
                          </p>
                        </td>
                        <td className="p-4">
                          <p>{o.address?.full_name}</p>
                          <p className="text-xs text-[#71717a]">{o.email}</p>
                        </td>
                        <td className="p-4">{currency(o.total)}</td>
                        <td className="p-4 text-[#52525b] uppercase text-xs">{o.payment_method}</td>
                        <td className="p-4">
                          <Select value={o.status} onValueChange={(v) => updateOrderStatus(o.id, v)}>
                            <SelectTrigger data-testid={`admin-order-status-${o.order_number}`} className="w-36 h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {["placed", "processing", "shipped", "delivered", "cancelled"].map((s) => (
                                <SelectItem key={s} value={s}>
                                  {s}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {orders.length === 0 && (
                  <p className="p-10 text-center text-sm text-[#71717a]">No orders yet.</p>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editing ? "Edit product" : "New product"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4 max-h-[70vh] overflow-y-auto">
            <div className="col-span-2">
              <Label>Name</Label>
              <Input
                data-testid="pform-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Slug</Label>
              <Input
                data-testid="pform-slug"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
              />
            </div>
            <div>
              <Label>Category</Label>
              <Input
                data-testid="pform-category"
                list="arialok-categories"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="Type or pick a category"
              />
              <datalist id="arialok-categories">
                {[...new Set([...categories, "Hair Oils", "Skincare", "Haircare", "Body", "Wellness", "Fragrance"])].map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              <p className="text-xs text-[#71717a] mt-1">
                Type any name to create a new category. It appears on the shop filters instantly.
              </p>
            </div>
            <div>
              <Label>Price</Label>
              <Input
                data-testid="pform-price"
                type="number"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
            </div>
            <div>
              <Label>Compare-at price</Label>
              <Input
                data-testid="pform-compare"
                type="number"
                value={form.compare_at_price ?? ""}
                onChange={(e) => setForm({ ...form, compare_at_price: e.target.value })}
              />
            </div>
            <div>
              <Label>Stock</Label>
              <Input
                data-testid="pform-stock"
                type="number"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })}
              />
            </div>
            <div>
              <Label>Featured</Label>
              <Select
                value={form.featured ? "yes" : "no"}
                onValueChange={(v) => setForm({ ...form, featured: v === "yes" })}
              >
                <SelectTrigger data-testid="pform-featured">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Product image</Label>
              <div className="mt-2">
                <ImageDropzone
                  value={form.image_url}
                  onChange={(url) => setForm({ ...form, image_url: url })}
                  testid="pform-image-dropzone"
                />
              </div>
              <Input
                data-testid="pform-image"
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                placeholder="…or paste an image URL"
                className="mt-2"
              />
            </div>
            <div className="col-span-2">
              <Label>Short description</Label>
              <Input
                data-testid="pform-short"
                value={form.short_description}
                onChange={(e) => setForm({ ...form, short_description: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <textarea
                data-testid="pform-desc"
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full p-3 border border-input rounded-md text-sm"
              />
            </div>
            <div className="col-span-2">
              <Label>Ingredients</Label>
              <Input
                data-testid="pform-ing"
                value={form.ingredients}
                onChange={(e) => setForm({ ...form, ingredients: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>Benefits (comma-separated)</Label>
              <Input
                data-testid="pform-benefits"
                value={benefitsInput}
                onChange={(e) => setBenefitsInput(e.target.value)}
                placeholder="Deep hydration, Softens skin"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="pform-save-btn"
              onClick={save}
              className="rounded-full bg-[#09090b] hover:bg-black text-white"
            >
              {editing ? "Save changes" : "Create product"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-white border border-[#e4e4e7] rounded-md p-5"
    >
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-widest text-[#71717a]">{label}</p>
        <span className="text-[#71717a]">{icon}</span>
      </div>
      <p className="mt-3 font-serif text-3xl text-[#09090b]">{value}</p>
    </div>
  );
}
